// src/App.js
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import LoginPage from "./components/LoginPage";
import Layout from "./Layout";
import ProtectedRoute from "./ProtectedRoute";
import WalletRouteGuard from "./components/WalletRouteGuard";
import PageRouteGuard from "./components/PageRouteGuard";

// ✅ Pages
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import Artists from "./pages/Artists";
import Mentors from "./pages/Mentors";
import Roles from "./pages/Roles";
import Permission from "./pages/Permission";
import Employees from "./pages/Employees";
import News from "./pages/News";
import ArtistReports from "./pages/ArtistReports";
import AllArtistJoiningReport from "./pages/AllArtistJoiningReport";
import PublicArtistRegistration from "./pages/PublicArtistRegistration";
import ArtistBookings from "./pages/ArtistBookings";
import MentorBookings from "./pages/MentorBookings";
import ApprovedEvents from "./pages/ApprovedEvents";
import BookedEvents from "./pages/BookedEvents";
import Posts from "./pages/Posts";
import Story from "./pages/Story";
import SongRequest from "./pages/SongRequest";
import Broadcast from "./pages/Broadcast";
import AppAdds from "./pages/AppAdds";
import ArtistVerification from "./pages/ArtistVerification";
import ChatSupport from "./pages/ChatSupport";
import ChatSupportAudit from "./pages/ChatSupportAudit";
import EventManagers from "./pages/EventManagers";
import WalletList from "./pages/WalletList";
import WalletDetail from "./pages/WalletDetail";
import WalletAudit from "./pages/WalletAudit";
import WithdrawRequests from "./pages/WithdrawRequests";
import OvertimeMonitoring from "./pages/OvertimeMonitoring";
import Settings from "./pages/Settings";
import HandlePostStoryReel from "./pages/HandlePostStoryReel";
import LegalAgreement from "./pages/LegalAgreement";
import Opportunities from "./pages/Opportunities";
import EventDiscovery from "./pages/EventDiscovery";
import CrmManager from "./pages/CrmManager";
import MailApnaArtist from "./pages/MailApnaArtist";
import PostInApp from "./pages/PostInApp";
import TvDisplay from "./pages/tv-disply/TvDisplay";


// ✅ NEW: Combined approvals page (events + venues)

// ✅ NEW: App Event Banners page (make sure the file exists at src/pages/app_event_banner.js)
import EventAppBanners from "./pages/app_event_banner";
import AppHomeBanner from "./pages/app_home_banner";

import AppMediaMonitor from "./pages/app_media_monitor";
import CreateRewardsAndOffer from "./pages/CreateRewardsAndOffer";

// 👉 Events area
import EventDashboard from "./events/event_dashboard";
import EventsLogin from "./events/events_login";
import EventProtectedRoute from "./events/EventProtectedRoute";
import EventsSignup from "./events/events_signup";
import CreateEventForm1 from "./events/create_event_form1";
import CreateEventForm2 from "./events/create_event_form2";
import PublicEventBooking from "./events/public_event_booking";

// 👉 Wire the dashboard cards
import ManageEventsDashboard from "./events/manage_events_dashboard";
import ManageVenuesDashboard from "./events/manage_venues_dashboard";

// 👇 NEW: PUBLIC invite form (no auth)
import OrganiserArtistInviteForm from "./events/oraganiser_artist_invite_form";

// 👇 NEW: Venue sub-pages (dummy pages)
import MangeVenueDetsils from "./events/mange_venue_detsils";
import VenueBookingDetails from "./events/venue_booking_details";
import CompletedVenueBookings from "./events/completed_venue_bookings";

function App() {
  const withPerm = (element, required, requireAll = false) => (
    <PageRouteGuard required={required} requireAll={requireAll}>
      {element}
    </PageRouteGuard>
  );

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        {/* 🔓 Public Login Route (OLD app) */}
        <Route path="/" element={<LoginPage />} />
        <Route path="/artist-registration" element={<PublicArtistRegistration />} />

        {/* 👉 Events: helper redirect so /events → /events/event_dashboard */}
        <Route path="/events" element={<Navigate to="/events/event_dashboard" replace />} />

        {/* 👉 Public: Organiser → Artist Invite Form (primary path under /events) */}
        <Route path="/events/oraganiser_artist_invite_form" element={<OrganiserArtistInviteForm />} />
        {/* 👉 Optional root-level alias so /oraganiser_artist_invite_form also works */}
        <Route path="/oraganiser_artist_invite_form" element={<OrganiserArtistInviteForm />} />

        {/* 👉 Events: login (mobile/password or OTP) */}
        <Route path="/events/login" element={<EventsLogin />} />
        <Route path="/events/book" element={<PublicEventBooking />} />
        <Route path="/book-event" element={<PublicEventBooking />} />

        {/* 👉 Events: signup and data save */}
        <Route path="/events/signup" element={<EventsSignup />} />

        {/* 👉 Events: protected routes */}
        <Route
          path="/events/event_dashboard"
          element={
            <EventProtectedRoute>
              <EventDashboard />
            </EventProtectedRoute>
          }
        />
        <Route
          path="/events/manage"
          element={
            <EventProtectedRoute>
              <ManageEventsDashboard />
            </EventProtectedRoute>
          }
        />
        <Route
          path="/events/venues"
          element={
            <EventProtectedRoute>
              <ManageVenuesDashboard />
            </EventProtectedRoute>
          }
        />
        {/* NEW: Venue sub-routes for tabs */}
        <Route
          path="/events/venues/manage"
          element={
            <EventProtectedRoute>
              <MangeVenueDetsils />
            </EventProtectedRoute>
          }
        />
        <Route
          path="/events/venues/bookings"
          element={
            <EventProtectedRoute>
              <VenueBookingDetails />
            </EventProtectedRoute>
          }
        />
        <Route
          path="/events/venues/completed"
          element={
            <EventProtectedRoute>
              <CompletedVenueBookings />
            </EventProtectedRoute>
          }
        />

        {/* Optional: a working placeholder for scan page */}
        <Route
          path="/events/scan"
          element={
            <EventProtectedRoute>
              <div style={{ padding: 24 }}>Scan Tickets — Coming soon</div>
            </EventProtectedRoute>
          }
        />

        {/* 👉 Events: Create Event (step 1 & step 2) */}
        <Route
          path="/events/create_event_form1"
          element={
            <EventProtectedRoute>
              <CreateEventForm1 />
            </EventProtectedRoute>
          }
        />
        <Route
          path="/events/create_event_form2"
          element={
            <EventProtectedRoute>
              <CreateEventForm2 />
            </EventProtectedRoute>
          }
        />

        <Route path="/admin/mail-apnaartist" element={<Navigate to="/panel/mail-apnaartist" replace />} />
        {/* 🔐 Protected Routes under /panel (OLD app) */}
        <Route
          path="/panel"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* 👇 All nested pages */}
          <Route path="dashboard" element={withPerm(<Dashboard />, "view_dashboard")} />
          <Route path="users" element={withPerm(<Users />, "view_users")} />
          <Route path="artists" element={withPerm(<Artists />, "view_artists")} />
          <Route path="mentors" element={withPerm(<Mentors />, "view_mentors")} />
          <Route path="roles" element={withPerm(<Roles />, "view_roles")} />
          <Route path="permission" element={withPerm(<Permission />, "view_permission")} />
          <Route path="employees" element={withPerm(<Employees />, "view_employees")} />
          <Route path="news" element={withPerm(<News />, "view_news")} />
          <Route path="artist-reports" element={withPerm(<ArtistReports />, "view_artist_reports")} />
          <Route
            path="all-artist-joining-report"
            element={(
              <PageRouteGuard
                required={["view_artists", "view_artist_reports", "manage_artists"]}
                allowAdminRole
              >
                <AllArtistJoiningReport />
              </PageRouteGuard>
            )}
          />
          <Route path="artist-bookings" element={withPerm(<ArtistBookings />, "view_artist_bookings")} />
          <Route path="mentor-bookings" element={withPerm(<MentorBookings />, "view_mentor_bookings")} />
          <Route path="approved-events" element={withPerm(<ApprovedEvents />, "view_approved_events")} />
          <Route path="booked-events" element={withPerm(<BookedEvents />, "view_booked_events")} />
          <Route path="posts" element={withPerm(<Posts />, "view_posts")} />
          <Route path="story" element={withPerm(<Story />, "view_story")} />
          <Route path="song-request" element={withPerm(<SongRequest />, "view_song_request")} />
          <Route path="broadcast" element={withPerm(<Broadcast />, "view_broadcast")} />
          <Route path="app-adds" element={withPerm(<AppAdds />, "view_app_adds")} />
          <Route
            path="artist-verification"
            element={withPerm(<ArtistVerification mode="verification" />, [
              "view_artist_verification",
              "verify_artist",
              "manage_artists",
              "manage_artist_pro_profiles",
              "moderate_artist_media",
            ])}
          />
          <Route
            path="verified-artists"
            element={withPerm(<ArtistVerification mode="verified" />, [
              "view_verified_artists",
              "view_artists",
              "manage_artists",
              "manage_artist_pro_profiles",
              "moderate_artist_media",
              "feature_artists",
            ])}
          />
          <Route path="chat-support" element={withPerm(<ChatSupport />, "view_chat_support")} />
          <Route path="chat-support-audit" element={withPerm(<ChatSupportAudit />, "view_chat_support")} />
          <Route path="event-managers" element={withPerm(<EventManagers />, "view_event_managers")} />
          <Route
            path="wallets"
            element={(
              <WalletRouteGuard>
                <WalletList />
              </WalletRouteGuard>
            )}
          />
          <Route
            path="wallets/:phone"
            element={(
              <WalletRouteGuard>
                <WalletDetail />
              </WalletRouteGuard>
            )}
          />
          <Route
            path="wallet-audit"
            element={(
              <WalletRouteGuard>
                <WalletAudit />
              </WalletRouteGuard>
            )}
          />
          <Route
            path="withdraw-requests"
            element={(
              <WalletRouteGuard>
                <WithdrawRequests />
              </WalletRouteGuard>
            )}
          />
          <Route
            path="overtime-monitoring"
            element={withPerm(<OvertimeMonitoring />, [
              "view_overtime_monitoring",
              "manage_overtime_monitoring",
              "wallet_admin",
            ])}
          />

          {/* ✅ NEW: Approved Events & Venues (combined page) */}
          <Route
            path="approved_events_venues"
            element={<Navigate to="/panel/event-discovery" replace />}
          />
          <Route
            path="event-discovery"
            element={withPerm(<EventDiscovery />, [
              "view_event_discovery",
              "manage_event_discovery",
              "feature_events",
              "view_events_venues_approval",
              "approve_events",
              "edit_event_status",
            ])}
          />

          {/* ✅ NEW: App Event Banners */}
          <Route
            path="manage-crm"
            element={withPerm(<CrmManager />, [
              "view_crm",
              "manage_crm",
              "manage_crm_contacts",
              "manage_crm_templates",
              "send_crm_broadcast",
              "view_crm_reports",
            ])}
          />
          <Route
            path="crm"
            element={<Navigate to="/panel/manage-crm" replace />}
          />
          <Route
            path="mail-apnaartist"
            element={withPerm(<MailApnaArtist />, [
              "view_crm",
              "manage_crm",
              "manage_crm_contacts",
              "manage_crm_templates",
              "send_crm_broadcast",
              "view_crm_reports",
            ])}
          />
          <Route
            path="post-in-app"
            element={withPerm(<PostInApp />, [
              "view_posts",
              "view_story",
              "view_opportunities",
              "view_artist_bookings",
              "view_mentor_bookings",
              "view_song_request",
              "view_booked_events",
            ])}
          />
          <Route
            path="app_event_banner"
            element={withPerm(<EventAppBanners />, [
              "view_event_app_banners",
              "manage_event_app_banners",
            ])}
          />
          <Route
            path="app_home_banner"
            element={withPerm(<AppHomeBanner />, [
              "view_event_app_banners",
              "manage_event_app_banners",
            ])}
          />
          <Route
            path="tv-display"
            element={(
              <PageRouteGuard
                required={[
                  "view_settings",
                  "manage_settings",
                  "view_dashboard",
                ]}
                allowAdminRole
              >
                <TvDisplay />
              </PageRouteGuard>
            )}
          />

          <Route path="app_media_monitor" element={withPerm(<AppMediaMonitor />, "view_media_monitor")} />
          <Route
            path="opportunities"
            element={withPerm(<Opportunities mode="list" />, [
              "view_opportunities",
              "manage_opportunities",
              "moderate_opportunities",
              "feature_opportunities",
            ])}
          />
          <Route
            path="opportunities/moderation"
            element={withPerm(<Opportunities mode="moderation" />, [
              "view_opportunities",
              "manage_opportunities",
              "moderate_opportunities",
              "feature_opportunities",
            ])}
          />
          <Route path="create_rewards_and_offer" element={<CreateRewardsAndOffer />} />
          <Route path="legal-agreement" element={<LegalAgreement />} />
          <Route
            path="settings"
            element={withPerm(<Settings />, [
              "view_settings",
              "manage_settings",
              "manage_payment_settings",
              "manage_api_settings",
              "manage_authkey_settings",
            ])}
          />

          {/* ➡ Default redirect to dashboard */}
          <Route
            path="handle-post-story-reel"
            element={withPerm(<HandlePostStoryReel />, [
              "view_settings",
              "manage_settings",
            ])}
          />
          <Route index element={<Navigate to="/panel/dashboard" />} />
        </Route>

        {/* 🚫 Unknown route fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
